import { priceSetupTabs } from "@nexxt/contracts/catalog";
import { plainDecimal as plain } from "@nexxt/ui";
import type { FinanceSetup, OperationsSetup, PriceSetupOptions, SalesSetup } from "./price-setup.queries";

// The forms of the Price Setup, as data (pattern.md §10): what each tab shows of what the catalog holds, and what a
// save sends. Every control holds text; a tab is saved whole. No React here: the components render it.

/** The tabs of the screen: the three functions of a season, and Finance's intercompany prices (ADR-249). */
export const setupScreenTabs = [...priceSetupTabs, "intercompany"] as const;
export type ScreenTab = (typeof setupScreenTabs)[number];

/** An emptied value is "none" for the catalog. */
const orNull = (text: string) => (text.trim() === "" ? null : text.trim());

export interface FinanceValues {
  fxRates: { from: string; to: string; rate: string }[];
}

export function financeValues(tab: FinanceSetup | null): FinanceValues {
  return { fxRates: (tab?.fxRates ?? []).map((r) => ({ from: r.from, to: r.to, rate: plain(r.rate) })) };
}

/** A pair of currencies has one rate, between two different currencies: the catalog refuses anything else. */
export function ratesAreConsistent({ fxRates }: FinanceValues): boolean {
  const pairs = fxRates.map((r) => `${r.from}/${r.to}`);
  return fxRates.every((r) => r.from !== r.to) && new Set(pairs).size === pairs.length;
}

export const operationsCosts = [
  "freightInPct",
  "dutiesInPct",
  "warehousingPct",
  "freightOutPct",
  "dutiesOutPct",
] as const satisfies readonly (keyof OperationsSetup)[];
export type OperationsValues = Record<(typeof operationsCosts)[number], string>;

export function operationsValues(tab: OperationsSetup | undefined): OperationsValues {
  return Object.fromEntries(operationsCosts.map((field) => [field, plain(tab?.[field])])) as OperationsValues;
}

export const salesTerms = [
  "distributionDiscountPct",
  "tomorrowMarginPct",
  "retailMultiplier",
  "extraMarginPct",
] as const satisfies readonly (keyof SalesSetup)[];

export interface SalesValues extends Record<(typeof salesTerms)[number], string> {
  isActive: boolean;
  currencies: { currency: string; extraMarginPct: string }[];
}

export function salesValues(tab: SalesSetup | undefined): SalesValues {
  return {
    isActive: tab?.isActive ?? true,
    ...(Object.fromEntries(salesTerms.map((field) => [field, plain(tab?.[field])])) as Record<
      (typeof salesTerms)[number],
      string
    >),
    currencies: (tab?.currencies ?? []).map((c) => ({ currency: c.currency, extraMarginPct: plain(c.extraMarginPct) })),
  };
}

/** What a save of Sales' tab sends: the whole tab, an emptied term as null. */
export function salesBody({ isActive, currencies, ...terms }: SalesValues) {
  return {
    isActive,
    ...(Object.fromEntries(salesTerms.map((field) => [field, orNull(terms[field])])) as Record<
      (typeof salesTerms)[number],
      string | null
    >),
    currencies,
  };
}

/** Whether the form holds something the catalog does not: both sides are text, in the same form. */
export const isDirty = <V>(initial: V, values: V): boolean => JSON.stringify(initial) !== JSON.stringify(values);

/**
 * Where a tab stands: Sales' also says when the price list is not sold for the brand. One still to confirm after the
 * last reminder of its function and a cadence stays highlighted (B-43, ADR-245).
 */
export type TabStatus = "none" | "saved" | "reminders_over" | "confirmed" | "inactive";

export function tabStatus(
  tab: { confirmedAt: string | null; isActive?: boolean } | null | undefined,
  remindersOver = false,
): TabStatus {
  if (!tab) return "none";
  if (tab.isActive === false) return "inactive";
  if (tab.confirmedAt) return "confirmed";
  return remindersOver ? "reminders_over" : "saved";
}

/** One row per price list, in their order, each with the tabs the brand has for it. */
export interface BrandTabRow {
  priceList: PriceSetupOptions["priceLists"][number];
  operations: OperationsSetup | undefined;
  sales: SalesSetup | undefined;
  /**
   * Whether the reminders of each tab are over for this row (B-43, ADR-245): the costs of Operations count only for a
   * price list whose sales tab is active, as the reminders do.
   */
  remindersOver: { operations: boolean; sales: boolean };
}

export function brandTabRows(
  priceLists: PriceSetupOptions["priceLists"],
  tabs: {
    operations: readonly OperationsSetup[];
    sales: readonly SalesSetup[];
    remindersOver?: { operations: boolean; sales: boolean };
  },
  brandOrgId: string,
): BrandTabRow[] {
  const of = <T extends { brandOrgId: string; priceList: string }>(list: readonly T[], code: string) =>
    list.find((t) => t.brandOrgId === brandOrgId && t.priceList === code);
  return priceLists.map((priceList) => {
    const sales = of(tabs.sales, priceList.code);
    return {
      priceList,
      operations: of(tabs.operations, priceList.code),
      sales,
      remindersOver: {
        operations: (tabs.remindersOver?.operations ?? false) && sales?.isActive === true,
        sales: tabs.remindersOver?.sales ?? false,
      },
    };
  });
}
